import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PsychologyPackages } from 'constants/psychologyPackages'
import { SurveyData } from 'redux/slices/onboarding-slice'
import { consultationApplyCoupon } from 'redux/thunks/consultation-apply-coupon'
import { consultationCheckout } from 'redux/thunks/consultation-checkout'
import { createConsultation } from 'redux/thunks/create-consultation'
import { generatePixCode } from 'redux/thunks/generate-pix-code'
import { getDoctors } from 'redux/thunks/get-doctors'
import { getSurvey } from 'redux/thunks/get-survey'
import { payWithCredits } from 'redux/thunks/pay-with-credits'
import { purchaseWithExistingCard } from 'redux/thunks/purchase-with-existing-card'
import { PaymentResponse } from 'redux/types'
import { Consultation, Doctor, DoctorCommunicationChannel } from 'types'

/**
 * Small-form (live flow) patient details, carried from the 'patient-info' step
 * through create-consultation and into the post-payment 'create-account' step.
 */
export interface NewConsultationPatientAttributes {
  firstName: string
  lastName: string
  completeName: string
  email: string
  cpfNumber: string
  phoneNumber: string
  termsOfService: boolean
}

export interface NewConsultationState {
  isLoading: boolean
  doctors: Doctor[]
  chosenDoctor: Doctor | null
  chosenPsychologyPackage: PsychologyPackages | null
  communicationChannel: DoctorCommunicationChannel | null
  consultation: Consultation | null
  appointmentAt: string | null
  pixQrCode: string | null
  paymentResponse: PaymentResponse | null
  appliedCoupon: { total: number } | null
  surveyData: SurveyData | null
  surveySubmission: Record<string, any> | null
  patientAttributes: NewConsultationPatientAttributes | null
  requestVersion: number
}

const initialState: NewConsultationState = {
  isLoading: false,
  doctors: [],
  chosenDoctor: null,
  chosenPsychologyPackage: null,
  communicationChannel: null,
  consultation: null,
  appointmentAt: null,
  pixQrCode: null,
  paymentResponse: null,
  appliedCoupon: null,
  surveyData: null,
  surveySubmission: null,
  patientAttributes: null,
  requestVersion: 0
}

export const newConsultationSlice = createSlice({
  name: 'new-consultation',
  initialState,
  reducers: {
    resetNewConsultationState: (state) => {
      const nextVersion = state.requestVersion + 1
      // Preserve small-form patient attributes so the live flow can still create
      // the account post-payment (handleCreateConsultation resets right before creating).
      const preservedPatientAttributes = state.patientAttributes
      Object.assign(state, {
        ...initialState,
        patientAttributes: preservedPatientAttributes,
        requestVersion: nextVersion
      })
    },
    setChosenPsychologyPackage: (state, action: PayloadAction<PsychologyPackages | null>) => {
      state.chosenPsychologyPackage = action.payload
    },
    setCommunicationChannel: (state, action: PayloadAction<DoctorCommunicationChannel | null>) => {
      state.communicationChannel = action.payload
    },
    setChosenDoctor: (state, { payload }: PayloadAction<Doctor | null>) => {
      state.chosenDoctor = payload
    },
    setAppointmentAt: (state, { payload }: PayloadAction<string | null>) => {
      state.appointmentAt = payload
    },
    setConsultation: (state, { payload }: PayloadAction<Consultation | null>) => {
      state.consultation = payload
    },
    setSurveySubmission: (state, { payload }: PayloadAction<Record<string, string> | null>) => {
      state.surveySubmission = payload
    },
    setNewConsultationPatientAttributes: (
      state,
      { payload }: PayloadAction<NewConsultationPatientAttributes | null>
    ) => {
      state.patientAttributes = payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getDoctors.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getDoctors.fulfilled, (state, action) => {
        state.isLoading = false
        state.doctors = action.payload
      })
      .addCase(getDoctors.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(createConsultation.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createConsultation.fulfilled, (state, { payload }) => {
        // Only accept if this response matches the current version (no stale in-flight overwrite)
        if (payload._requestVersion !== undefined && payload._requestVersion !== state.requestVersion) return
        state.isLoading = false
        state.consultation = payload
      })
      .addCase(createConsultation.rejected, (state) => {
        state.isLoading = false
        state.consultation = null
      })
      .addCase(generatePixCode.pending, (state) => {
        state.isLoading = true
      })
      .addCase(generatePixCode.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.pixQrCode = payload.qrcode
      })
      .addCase(generatePixCode.rejected, (state) => {
        state.isLoading = false
        state.pixQrCode = null
      })
      .addCase(consultationCheckout.pending, (state) => {
        state.isLoading = true
      })
      .addCase(consultationCheckout.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.paymentResponse = payload
      })
      .addCase(consultationCheckout.rejected, (state) => {
        state.isLoading = false
        state.paymentResponse = null
      })
      .addCase(consultationApplyCoupon.pending, (state) => {
        state.isLoading = true
      })
      .addCase(consultationApplyCoupon.fulfilled, (state, { payload }) => {
        const { total } = payload
        state.isLoading = false
        state.appliedCoupon = {
          total: parseFloat(total)
        }
      })
      .addCase(consultationApplyCoupon.rejected, (state) => {
        state.isLoading = false
        state.appliedCoupon = null
      })
      .addCase(purchaseWithExistingCard.pending, (state) => {
        state.isLoading = true
      })
      .addCase(purchaseWithExistingCard.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.paymentResponse = payload
      })
      .addCase(purchaseWithExistingCard.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(payWithCredits.pending, (state) => {
        state.isLoading = true
      })
      .addCase(payWithCredits.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.paymentResponse = payload
      })
      .addCase(payWithCredits.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(getSurvey.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getSurvey.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.surveyData = payload
      })
      .addCase(getSurvey.rejected, (state) => {
        state.isLoading = false
      })
})

export const {
  resetNewConsultationState,
  setChosenPsychologyPackage,
  setCommunicationChannel,
  setChosenDoctor,
  setAppointmentAt,
  setConsultation,
  setSurveySubmission,
  setNewConsultationPatientAttributes
} = newConsultationSlice.actions

export default newConsultationSlice.reducer
